import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";

import useToastActionWrapper from "../../../hooks/useToastActionWrapper";

import { updateEMPDetails } from "../../../services/clearing/emps";

import LanguageDropdown, {
  LANGUGAGE_OPTIONS,
} from "../../../components/inputs/LanguageDropdown";
import { ISO_COUNTRY_OPTIONS, DUE_IN_DAYS_OPTIONS, VAT_OPTION_OPTIONS } from "../../../components/inputs/consts";
import CountryDropdown from "../../../components/inputs/CountryDropdown";
import Dropdown from "../../../components/inputs/Dropdown";

import EmpBillsGenerationToolbar from "../components/EmpBillsGenerationToolbar/EmpBillsGenerationToolbar";
import TextInputField from "../../../components/inputs/TextInputField";
import { EMPValidationSchema } from "../validation";
import { Checkbox } from "primereact/checkbox";

import { DeleteEMPSection } from "../components/DeleteEMPSection";

const fallbackEMPDetails = {
  mandantName: "",
  actualEmps: 1,
  iban: "",
  bic: "",
  billingAddressAdditional: "",
  billingAddressCity: "",
  billingAddressCompanyName: "",
  billingAddressCountry: "DE",
  billingAddressHouseNumber: "",
  billingAddressPostcode: "",
  billingAddressStreet: "",
  businessAddressAdditional: "",
  businessAddressCity: "",
  businessAddressCompanyName: "",
  businessAddressCountry: "DE",
  businessAddressHouseNumber: "",
  businessAddressPostcode: "",
  businessAddressStreet: "",
  vatOption: "VAT_DEFAULT",
  email: "",
  invoiceEmail: "",
  invoiceLanguage: "DE",
  notes: "",
  taxId: "",
  vatId: "",
  dueInDays: null,
};

const EditEMP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const details = location?.state?.details ?? fallbackEMPDetails;

  const toast = useRef(null);
  const { isLoading } = useToastActionWrapper(toast);
  const [empDetails, setEMPDetails] = useState<Record<string, any>>(details);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

  const [showBillingAddressFields, setShowBillingAddressFields] = useState(
    empDetails?.billingAddressCity !== ""
  );

  const validateField = async (fieldName: string, value: string) => {
    try {
      await EMPValidationSchema?.validateAt(fieldName, { [fieldName]: value });
      setValidationErrors((prev) => ({ ...prev, [fieldName]: null }));
    } catch (error) {
      setValidationErrors((prev) => ({ ...prev, [fieldName]: error?.message }));
    }
  };

  const handleChange = (e, field) => {
    const value = "target" in e ? e?.target?.value : e?.value;

    setEMPDetails((prev) => {
      validateField(field, value);
      return { ...prev, [field]: value };
    });
  };

  const handleUpdate = async () => {
    try {
      const updated = await updateEMPDetails(empDetails?.mandantName, empDetails);
      if (updated) {
        navigate("/customers/emps");
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update EMP details.",
        life: 3000,
      });
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} className="z-10" />

      <EmpBillsGenerationToolbar mandateName={empDetails?.mandantName} invoiceEmail={empDetails?.invoiceEmail} />

      <Card
        className="p-4"
        title="EMP Details"
        subTitle={empDetails?.billingAddressCompanyName}
      >
        <div className="pt-2 p-fluid grid formgrid">
          {isLoading && (
            <div className="field col-12 flex justify-content-center my-5">
              <ProgressSpinner />
            </div>
          )}

          <TextInputField
            name="mandantName"
            label="Mandant Name"
            required
            value={empDetails?.mandantName || ""}
            onChange={(e) => handleChange(e, "mandantName")}
            errorMessage={validationErrors?.mandantName || null}
          />

          <div className="field col-12 md:col-6">
            <label className="input-label">Easybill Contact ID</label>
            <div className="font-bold text-lg py-2">{empDetails?.easybillContactId || "-"}</div>
          </div>

          <TextInputField
            name="email"
            label="Email"
            required
            value={empDetails?.email || ""}
            onChange={(e) => handleChange(e, "email")}
            errorMessage={validationErrors?.email || null}
          />

          <TextInputField
            name="invoiceEmail"
            label="Invoice Email"
            required
            value={empDetails?.invoiceEmail || null}
            onChange={(e) => handleChange(e, "invoiceEmail")}
            errorMessage={validationErrors?.invoiceEmail || null}
          />

          <TextInputField
            name="taxId"
            label="Tax ID"
            required
            value={empDetails?.taxId || ""}
            onChange={(e) => handleChange(e, "taxId")}
            errorMessage={validationErrors?.taxId || null}
          />

          <TextInputField
            name="vatId"
            label="VAT ID"
            value={empDetails?.vatId}
            onChange={(e) => handleChange(e, "vatId")}
            errorMessage={validationErrors?.vatId}
          />

          <TextInputField
            name="iban"
            label="IBAN"
            required
            value={empDetails?.iban || ""}
            onChange={(e) => handleChange(e, "iban")}
            errorMessage={validationErrors?.iban || null}
          />

          <TextInputField
            name="bic"
            label="BIC"
            value={empDetails?.bic || ""}
            onChange={(e) => handleChange(e, "bic")}
            errorMessage={validationErrors?.bic || null}
          />

          <div className="field col-12 md:col-6">
            <label className="input-label">Invoice Language *</label>
            <LanguageDropdown
              value={LANGUGAGE_OPTIONS.find(
                (option) =>
                  option?.code === (empDetails?.invoiceLanguage || "DE")
              )}
              onChange={(value) =>
                handleChange({ value: value?.code || null }, "invoiceLanguage")
              }
              placeholder="Select a language"
              className="input-field"
            />
          </div>

          <TextInputField
            name="notes"
            label="Notes"
            value={empDetails?.notes || ""}
            onChange={(e) => handleChange(e, "notes")}
            errorMessage={validationErrors?.notes || null}
            containerClassName="field col-12"
          />

          {/* Business Address Section */}
          <div className="field col-12">
            <h3 className="mb-2">Business Address</h3>
          </div>
          <TextInputField
            name="businessAddressCompanyName"
            label="Company Name"
            required
            value={empDetails?.businessAddressCompanyName || ""}
            onChange={(e) => handleChange(e, "businessAddressCompanyName")}
            errorMessage={validationErrors?.businessAddressCompanyName || null}
          />
          <TextInputField
            name="businessAddressStreet"
            label="Street"
            required
            value={empDetails?.businessAddressStreet || ""}
            onChange={(e) => handleChange(e, "businessAddressStreet")}
            errorMessage={validationErrors?.businessAddressStreet || null}
          />
          <TextInputField
            name="businessAddressHouseNumber"
            label="House Number"
            value={empDetails?.businessAddressHouseNumber || ""}
            onChange={(e) => handleChange(e, "businessAddressHouseNumber")}
            errorMessage={validationErrors?.businessAddressHouseNumber || null}
            containerClassName="field col-12 md:col-4"
          />
          <TextInputField
            name="businessAddressPostcode"
            label="Postal Code"
            required
            value={empDetails?.businessAddressPostcode || ""}
            onChange={(e) => handleChange(e, "businessAddressPostcode")}
            errorMessage={validationErrors?.businessAddressPostcode || null}
            containerClassName="field col-12 md:col-4"
          />
          <TextInputField
            name="businessAddressCity"
            label="City"
            required
            value={empDetails?.businessAddressCity || ""}
            onChange={(e) => handleChange(e, "businessAddressCity")}
            errorMessage={validationErrors?.businessAddressCity || null}
            containerClassName="field col-12 md:col-4"
          />
          <div className="field col-12 md:col-6">
            <label className="input-label">Country *</label>
            <CountryDropdown
              value={
                ISO_COUNTRY_OPTIONS.find(
                  (option) => option.code === empDetails?.businessAddressCountry
                ) || null
              }
              onChange={(value) =>
                handleChange(
                  { value: value?.code || null },
                  "businessAddressCountry"
                )
              }
              className="input-field"
            />
          </div>
          <TextInputField
            name="businessAddressAdditional"
            label="Additional Address Info"
            value={empDetails?.businessAddressAdditional || ""}
            onChange={(e) => handleChange(e, "businessAddressAdditional")}
            errorMessage={validationErrors?.businessAddressAdditional || null}
          />

          {/* Billing Address Section */}
          <div className="field col-12 pb-3 flex flex-row align-items-center justify-content-start">
            <Checkbox
              checked={showBillingAddressFields}
              onChange={() => setShowBillingAddressFields((p) => !p)}
            />
            <label className="input-label m-2">
              Billing Address (Optional)
            </label>
          </div>

          {showBillingAddressFields && (
            <>
              <div className="field col-12">
                <h3 className="mb-2">Billing Address</h3>
              </div>
              <TextInputField
                name="billingAddressCompanyName"
                label="Company Name"
                value={empDetails?.billingAddressCompanyName || ""}
                onChange={(e) => handleChange(e, "billingAddressCompanyName")}
                errorMessage={
                  validationErrors?.billingAddressCompanyName || null
                }
              />
              <TextInputField
                name="billingAddressStreet"
                label="Street"
                value={empDetails?.billingAddressStreet || ""}
                onChange={(e) => handleChange(e, "billingAddressStreet")}
                errorMessage={validationErrors?.billingAddressStreet || null}
              />
              <TextInputField
                name="billingAddressHouseNumber"
                label="House Number"
                value={empDetails?.billingAddressHouseNumber || ""}
                onChange={(e) => handleChange(e, "billingAddressHouseNumber")}
                errorMessage={
                  validationErrors?.billingAddressHouseNumber || null
                }
                containerClassName="field col-12 md:col-4"
              />
              <TextInputField
                name="billingAddressPostcode"
                label="Postal Code"
                value={empDetails?.billingAddressPostcode || ""}
                onChange={(e) => handleChange(e, "billingAddressPostcode")}
                errorMessage={validationErrors?.billingAddressPostcode || null}
                containerClassName="field col-12 md:col-4"
              />
              <TextInputField
                name="billingAddressCity"
                label="City"
                value={empDetails?.billingAddressCity || ""}
                onChange={(e) => handleChange(e, "billingAddressCity")}
                errorMessage={validationErrors?.billingAddressCity || null}
                containerClassName="field col-12 md:col-4"
              />
              <div className="field col-12 md:col-6">
                <label className="input-label">Country</label>
                <CountryDropdown
                  value={
                    ISO_COUNTRY_OPTIONS.find(
                      (option) =>
                        option?.code === empDetails?.billingAddressCountry
                    ) || null
                  }
                  onChange={(value) =>
                    handleChange(
                      { value: value?.code || null },
                      "billingAddressCountry"
                    )
                  }
                  className="input-field"
                />
              </div>
              <TextInputField
                name="billingAddressAdditional"
                label="Additional Address Info"
                value={empDetails?.billingAddressAdditional || ""}
                onChange={(e) => handleChange(e, "billingAddressAdditional")}
                errorMessage={
                  validationErrors?.billingAddressAdditional || null
                }
              />
            </>
          )}

          {/* VAT Option */}
          <div className="field col-12 md:col-6">
            <label className="input-label">VAT Option</label>
            <Dropdown
              options={VAT_OPTION_OPTIONS}
              value={
                VAT_OPTION_OPTIONS.find(
                  (option) => option.code === empDetails?.vatOption
                ) || null
              }
              onChange={(value) =>
                handleChange({ value: value?.code || null }, "vatOption")
              }
              placeholder="Select VAT option"
              className="input-field"
            />
          </div>

          {/* Due in Days */}
          <div className="field col-12">
            <h3 className="mb-2">Due in Days</h3>
          </div>
          <div className="field col-12 md:col-6">
            <label className="input-label">Due in Days</label>
            <Dropdown
              options={DUE_IN_DAYS_OPTIONS}
              value={
                DUE_IN_DAYS_OPTIONS.find(
                  (option) => option.code === empDetails?.dueInDays
                ) || null
              }
              onChange={(value) =>
                handleChange({ value: value?.code || null }, "dueInDays")
              }
              placeholder="Select due in days"
              className="input-field"
            />
          </div>

          {/* Submit Button */}
          <div className="field flex flex-column col-12 md:flex-row md:justify-content-end gap-2">
            <Button
              label="Update EMP Details"
              onClick={handleUpdate}
              className="p-button w-full md:w-20rem"
              disabled={isLoading}
              loading={isLoading}
            />
          </div>
        </div>
      </Card>

      <DeleteEMPSection mandantName={empDetails?.mandantName} />
    </div>
  );
};

export default EditEMP;
